import { useEffect, useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { GuidelinePanel } from "./guideline-panel";

interface ChatInputProps {
  onSend: (content: string) => boolean | void | Promise<boolean>;
  resetVersion?: number;
  maxBytes?: number;
  showAttachment?: boolean;
  showGuideline?: boolean;
  disabled: boolean;
}

export function ChatInput({
  onSend,
  resetVersion,
  maxBytes = 4096,
  disabled,
  showAttachment = false,
  showGuideline = false,
}: ChatInputProps) {
  const [value, setValue] = useState("");
  const byteHintId = useId();
  const bytes = new TextEncoder().encode(value.trim()).length;
  const tooLong = bytes > maxBytes;
  useEffect(() => {
    setValue("");
  }, [resetVersion]);
  const [guidelineExpanded, setGuidelineExpanded] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
    }
  }, [value]);

  const handleSend = async () => {
    const trimmed = value.trim();
    if (!trimmed || disabled || tooLong) return;
    if ((await onSend(trimmed)) !== false) setValue("");
  };

  return (
    <div className="w-full shrink-0 rounded-nv-card border border-nv-border bg-nv-surface px-3.5 pb-2.5 pt-3 focus-within:ring-1 focus-within:ring-ring">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (
            event.key === "Enter" &&
            !event.shiftKey &&
            !event.nativeEvent.isComposing &&
            event.keyCode !== 229
          ) {
            event.preventDefault();
            handleSend();
          }
        }}
        aria-label="상담 메시지"
        aria-invalid={tooLong}
        aria-describedby={tooLong ? byteHintId : undefined}
        placeholder="메시지를 입력하세요…"
        maxLength={20000}
        disabled={disabled}
        rows={1}
        className="min-h-5 resize-none border-0 bg-transparent p-0 text-[14px] text-nv-text-primary"
      />
      {tooLong && (
        <p
          id={byteHintId}
          role="alert"
          className="mt-2 text-caption text-content-negative"
        >
          메시지는 {maxBytes.toLocaleString()}바이트까지 입력할 수 있어요. (
          {bytes.toLocaleString()}바이트)
        </p>
      )}
      <div className="mt-2.5 flex items-center justify-between">
        {showGuideline ? (
          <GuidelinePanel
            expanded={guidelineExpanded}
            onToggle={() => setGuidelineExpanded((expanded) => !expanded)}
          />
        ) : showAttachment ? (
          <span title="파일 첨부는 준비 중입니다">
            <Button
              type="button"
              variant="ghost"
              disabled
              className="h-auto bg-transparent p-0 text-caption text-content-medium disabled:bg-transparent"
              aria-label="첨부 (준비 중)"
            >
              ＋ 첨부
            </Button>
          </span>
        ) : (
          <span />
        )}
        <Button
          type="button"
          variant="primary"
          size="icon"
          onClick={handleSend}
          disabled={!value.trim() || disabled || tooLong}
          aria-label="메시지 보내기"
          className="h-[34px] w-[34px] rounded-full bg-nv-brand text-[16px] font-bold text-white hover:bg-nv-brand disabled:bg-nv-brand disabled:text-white"
        >
          ↑
        </Button>
      </div>
    </div>
  );
}
